//#region src/errors/EngineErrorCode.ts
const EngineErrorCode = {
	Unknown: "Unknown",
	InvalidArg: "InvalidArg",
	DocNotOpen: "DocNotOpen",
	DocOpenFailed: "DocOpenFailed",
	DocPasswordRequired: "DocPasswordRequired",
	DocPasswordIncorrect: "DocPasswordIncorrect",
	Aborted: "Aborted",
	Network: "Network",
	Unauthenticated: "Unauthenticated",
	Forbidden: "Forbidden",
	NotFound: "NotFound",
	WireFormat: "WireFormat",
	RuntimeUnavailable: "RuntimeUnavailable",
	/**
	* Annotation/page reference could not be resolved. Surfaced when:
	*   - `AnnotationRef.kind === 'objectNumber'` but the document has no
	*     annotation with that indirect object number on the addressed page
	*   - `AnnotationRef.kind === 'nm'` but no annotation on the page has
	*     that `/NM`
	*   - `AnnotationRef.kind === 'index'` but the supplied `RevisionToken`
	*     does not match the current per-page generation
	*   - `AnnotationRef.kind === 'index'` but `index` is out of range for
	*     the page's current annotation count
	*/
	InvalidReference: "InvalidReference",
	/**
	* A weak-annotation structural edit (delete/move) was attempted while the
	* caller does not have sole active edit presence for that page. This is a
	* collaboration guard for index-addressed annotations; stable pages and
	* non-shifting annotation updates do not use it.
	*/
	WeakAnnotationSessionConflict: "WeakAnnotationSessionConflict",
	/**
	* A layer write lost its optimistic-concurrency check: the layer's
	* durable version advanced (another writer — typically another server
	* replica — committed) between the operation's prepare and its commit,
	* and the server exhausted its rebase-and-retry budget. Retryable: the
	* operation was NOT applied; re-issue it against the new state.
	*/
	LayerVersionConflict: "LayerVersionConflict",
	/**
	* The requested operation is typed but not yet wired in this engine
	* version. The wire shape stays valid; clients can detect this with
	* `EngineError.is(err, EngineErrorCode.NotImplemented)` and degrade
	* gracefully.
	*/
	NotImplemented: "NotImplemented",
	/**
	* The PDF was loadable enough for PDFium to parse, but violates a
	* structural invariant the engine relies on. Surfaced for the rare
	* spec-violating PDF that ships a direct (non-indirect) page
	* dictionary in the /Pages tree — see `EPDFPage_GetObjectNumber`
	* returning `0`. PDFium creation paths (`FPDFPage_New`,
	* `EPDFPage_CreateAnnot`) always produce indirect objects, so this
	* error code is intentionally not used for engine-produced state;
	* it specifically signals "this input file is broken in a way we
	* cannot work around without compromising stable identity".
	*
	* Distinct from `DocOpenFailed` (PDFium refused to load the bytes
	* at all) and `NotFound` (a well-formed page/annotation/etc. simply
	* doesn't exist).
	*/
	MalformedPdf: "MalformedPdf"
};
//#endregion
//#region src/annotation/subtype.ts
/**
* Closed-world string catalog of annotation subtypes the v3 wire format knows
* about. The TypeScript discriminated union `AnnotationDTO` is keyed on this
* literal type. PDFium's integer subtype code is mapped onto these strings
* by the reader registry.
*
* `unsupported` is the forward-compat fallback for any PDF subtype that
* the engine has not implemented a reader for yet. Unknown subtypes never
* silently disappear from the wire — they round-trip as
* `{ subtype: 'unsupported', rawSubtypeCode }`.
*/
const ANNOTATION_SUBTYPES = [
	"text",
	"link",
	"free-text",
	"line",
	"square",
	"circle",
	"polygon",
	"polyline",
	"highlight",
	"underline",
	"squiggly",
	"strikeout",
	"stamp",
	"caret",
	"ink",
	"popup",
	"file-attachment",
	"redact",
	"widget",
	"unsupported"
];
/**
* PDFium subtype codes from `FPDFAnnot_GetSubtype`. Mirrors
* `FPDF_ANNOTATION_SUBTYPE` in `public/fpdf_annot.h`. We keep this here
* (rather than importing from @embedpdf/engine-runtime) so the engine-core
* package has zero PDFium dependency.
*/
const PdfAnnotationSubtypeCode = {
	UNKNOWN: 0,
	TEXT: 1,
	LINK: 2,
	FREETEXT: 3,
	LINE: 4,
	SQUARE: 5,
	CIRCLE: 6,
	POLYGON: 7,
	POLYLINE: 8,
	HIGHLIGHT: 9,
	UNDERLINE: 10,
	SQUIGGLY: 11,
	STRIKEOUT: 12,
	STAMP: 13,
	CARET: 14,
	INK: 15,
	POPUP: 16,
	FILEATTACHMENT: 17,
	SOUND: 18,
	MOVIE: 19,
	WIDGET: 20,
	SCREEN: 21,
	PRINTERMARK: 22,
	TRAPNET: 23,
	WATERMARK: 24,
	THREED: 25,
	RICHMEDIA: 26,
	XFAWIDGET: 27,
	REDACT: 28
};
/**
* Map from PDFium codes to wire-stable string subtypes. Codes the engine
* recognises as a v3 subtype (or maps to 'unsupported' if not yet wired).
*/
const PDF_CODE_TO_SUBTYPE = Object.freeze({
	[PdfAnnotationSubtypeCode.TEXT]: "text",
	[PdfAnnotationSubtypeCode.LINK]: "link",
	[PdfAnnotationSubtypeCode.FREETEXT]: "free-text",
	[PdfAnnotationSubtypeCode.LINE]: "line",
	[PdfAnnotationSubtypeCode.SQUARE]: "square",
	[PdfAnnotationSubtypeCode.CIRCLE]: "circle",
	[PdfAnnotationSubtypeCode.POLYGON]: "polygon",
	[PdfAnnotationSubtypeCode.POLYLINE]: "polyline",
	[PdfAnnotationSubtypeCode.HIGHLIGHT]: "highlight",
	[PdfAnnotationSubtypeCode.UNDERLINE]: "underline",
	[PdfAnnotationSubtypeCode.SQUIGGLY]: "squiggly",
	[PdfAnnotationSubtypeCode.STRIKEOUT]: "strikeout",
	[PdfAnnotationSubtypeCode.STAMP]: "stamp",
	[PdfAnnotationSubtypeCode.CARET]: "caret",
	[PdfAnnotationSubtypeCode.INK]: "ink",
	[PdfAnnotationSubtypeCode.POPUP]: "popup",
	[PdfAnnotationSubtypeCode.FILEATTACHMENT]: "file-attachment",
	[PdfAnnotationSubtypeCode.WIDGET]: "widget",
	[PdfAnnotationSubtypeCode.REDACT]: "redact"
});
/**
* Reverse map for write paths that need to turn a wire subtype back into the
* PDFium integer code without importing the zod-backed annotation registry.
*/
const PDF_SUBTYPE_TO_CODE = Object.freeze({
	text: PdfAnnotationSubtypeCode.TEXT,
	link: PdfAnnotationSubtypeCode.LINK,
	"free-text": PdfAnnotationSubtypeCode.FREETEXT,
	line: PdfAnnotationSubtypeCode.LINE,
	square: PdfAnnotationSubtypeCode.SQUARE,
	circle: PdfAnnotationSubtypeCode.CIRCLE,
	polygon: PdfAnnotationSubtypeCode.POLYGON,
	polyline: PdfAnnotationSubtypeCode.POLYLINE,
	highlight: PdfAnnotationSubtypeCode.HIGHLIGHT,
	underline: PdfAnnotationSubtypeCode.UNDERLINE,
	squiggly: PdfAnnotationSubtypeCode.SQUIGGLY,
	strikeout: PdfAnnotationSubtypeCode.STRIKEOUT,
	stamp: PdfAnnotationSubtypeCode.STAMP,
	caret: PdfAnnotationSubtypeCode.CARET,
	ink: PdfAnnotationSubtypeCode.INK,
	popup: PdfAnnotationSubtypeCode.POPUP,
	"file-attachment": PdfAnnotationSubtypeCode.FILEATTACHMENT,
	redact: PdfAnnotationSubtypeCode.REDACT,
	widget: PdfAnnotationSubtypeCode.WIDGET,
	unsupported: PdfAnnotationSubtypeCode.UNKNOWN
});
function subtypeFromCode(code) {
	return PDF_CODE_TO_SUBTYPE[code] ?? "unsupported";
}
//#endregion
export { subtypeFromCode as a, PdfAnnotationSubtypeCode as i, PDF_CODE_TO_SUBTYPE as n, EngineErrorCode as o, PDF_SUBTYPE_TO_CODE as r, ANNOTATION_SUBTYPES as t };

//# sourceMappingURL=subtype-BvAzLCGC.js.map